<template>
  <b-card>
    <div>
      <b-row class="custom-search align-items-center justify-content-end mb-1">
        <b-col cols="12" sm="8" md="6" lg="4">
          <b-form-input
            v-if="$permissionAbility(OUTREACH_AUTHOR_SHOW, permissions)"
            v-on:keyup="onSearch"
            v-model.lazy="searchTerm"
            placeholder="Search..."
            type="text"
            class="mb-2 mb-sm-0"
          />
        </b-col>

        <b-col cols="12" sm="auto" class="text-sm-right">
          <template v-if="$permissionAbility(OUTREACH_AUTHOR_CREATE, permissions)">
            <b-button
              class="flex-shrink-0 w-100 w-sm-auto"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              v-on:click="showCreateModal"
            >
              Create
            </b-button>
          </template>
        </b-col>
      </b-row>

      <vue-good-table
        v-if="$permissionAbility(OUTREACH_AUTHOR_SHOW, permissions)"
        styleClass="vgt-table table-custom-style striped clickable-rows"
        :line-numbers="false"
        mode="remote"
        @on-page-change="onPageChange"
        @on-sort-change="onSortChange"
        @on-column-filter="onColumnFilter"
        @on-per-page-change="onPerPageChange"
        @on-row-click="onRowClick"
        :totalRows="totalRecords"
        :isLoading.sync="isLoading"
        :rows="rows"
        :columns="columns"
        :sort-options="{
          enabled: false,
          multipleColumns: true,
          initialSortBy: [{ field: 'created_at', type: 'desc' }],
        }"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
      >
        <template slot="table-row" slot-scope="props">
          <!-- Status -->
          <template v-if="props?.column?.field === 'format_status'">
            <b-badge :variant="statusVariant(props?.row?.status)">
              {{ props?.row?.status }}
            </b-badge>
          </template>

          <!-- Contact -->
          <template v-if="props?.column?.field === 'format_contact'">
            <div v-if="props?.row?.contact">
              <span class="font-weight-bold">{{ props?.row?.contact }}</span>
            </div>
            <div v-else>
              <b-badge variant="light-warning">NA</b-badge>
            </div>
          </template>

          <!-- Posts Used -->
          <template v-if="props?.column?.field === 'format_posts'">
            <span>{{ props?.row?.posts_used }} / {{ props?.row?.total_posts_approved }}</span>
          </template>

          <!-- Action -->
          <span v-if="props.column.field === 'action'">
            <template v-if="props?.row?.status === 'pending' && $permissionAbility(OUTREACH_AUTHOR_REVIEW, permissions)">
              <span @click.stop="onStatusChange(props.row, 'approved')" v-b-tooltip.hover title="Approve">
                <feather-icon
                  icon="CheckCircleIcon"
                  class="mr-50 custom-icon cursor-pointer text-success"
                  size="16"
                />
              </span>
              <span @click.stop="onStatusChange(props.row, 'rejected')" v-b-tooltip.hover title="Reject">
                <feather-icon
                  icon="XCircleIcon"
                  class="mr-50 custom-icon cursor-pointer text-danger"
                  size="16"
                />
              </span>
            </template>
            <template v-if="$permissionAbility(OUTREACH_AUTHOR_SHOW, permissions)">
              <span @click.stop="viewDetail(props.row)">
                <feather-icon
                  v-b-tooltip.hover
                  icon="EyeIcon"
                  class="mr-50 custom-icon cursor-pointer"
                  title="View Detail"
                  size="16"
                />
              </span>
            </template>
            <template v-if="$permissionAbility(OUTREACH_AUTHOR_EDIT, permissions)">
              <span @click.stop="onEdit(props.row)">
                <feather-icon
                  v-b-tooltip.hover
                  icon="Edit2Icon"
                  class="mr-50 custom-icon cursor-pointer"
                  title="Edit"
                  size="16"
                />
              </span>
            </template>
            <template v-if="$permissionAbility(OUTREACH_AUTHOR_DELETE, permissions)">
              <span @click.stop="onDelete(props.row)">
                <feather-icon
                  v-b-tooltip.hover
                  icon="TrashIcon"
                  class="mr-50 custom-icon cursor-pointer"
                  title="Delete"
                  size="16"
                />
              </span>
            </template>
          </span>

          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>

        <template slot="pagination-bottom" slot-scope="props">
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap"> Showing 1 to </span>
              <b-form-select
                v-model="pageLength"
                :options="['10', '25', '50', '100', '500']"
                class="mx-1"
                @input="(value) => props.perPageChanged({ currentPerPage: value })"
              />
              <span class="text-nowrap"> of {{ props.total }} entries </span>
            </div>
            <div>
              <b-pagination
                :value="1"
                :total-rows="props.total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value) => props.pageChanged({ currentPage: value })"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
    </div>

    <!-- Create/Edit Modal -->
    <b-modal
      id="modal-author-form"
      centered
      :title="modelType == 'editModel' ? 'Edit Author' : 'Create Author'"
      hide-footer
      @hidden="hiddenModal"
      no-close-on-backdrop
      size="lg"
    >
      <validation-observer ref="authorValidation">
        <b-form v-on:submit.prevent="validationForm">
          <b-form-group label="Name" label-for="name" class="required-label">
            <validation-provider #default="{ errors }" name="Author Name" vid="name" rules="required|max:255">
              <b-form-input
                id="name"
                type="text"
                v-model="authorName"
                :state="errors.length > 0 ? false : null"
                placeholder="Author Name"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group label="Contact / Handle" label-for="contact">
            <validation-provider #default="{ errors }" name="Contact" vid="contact" rules="max:255">
              <b-form-input
                id="contact"
                type="text"
                v-model="authorContact"
                :state="errors.length > 0 ? false : null"
                placeholder="Email, Skype, or handle"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Websites -->
          <b-form-group label="Websites" label-for="websites" class="required-label">
            <div v-for="(site, index) in authorWebsites" :key="index" class="mb-2 p-2 border rounded">
              <b-form-group label="URL" class="mb-1">
                <div class="d-flex">
                  <b-form-input
                    v-model="site.url"
                    placeholder="https://example.com"
                    type="url"
                    class="mr-1"
                  />
                  <b-button variant="outline-danger" size="sm" @click="removeWebsite(index)" v-if="authorWebsites.length > 1">
                    <feather-icon icon="XIcon" />
                  </b-button>
                </div>
              </b-form-group>
              <b-row>
                <b-col md="3" sm="6" cols="12">
                  <b-form-group label="Domain Authority" class="mb-1">
                    <b-form-input
                      v-model="site.domain_authority"
                      type="number"
                      placeholder="Domain Authority"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="3" sm="6" cols="12">
                  <b-form-group label="Traffic" class="mb-1">
                    <b-form-input
                      v-model="site.traffic"
                      placeholder="Traffic"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="3" sm="6" cols="12">
                  <b-form-group label="Traffic Value ($)" class="mb-1">
                    <b-form-input
                      v-model="site.traffic_value"
                      type="number"
                      step="any"
                      placeholder="Traffic Value"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="3" sm="6" cols="12">
                  <b-form-group label="Spam Score (%)" class="mb-1">
                    <b-form-input
                      v-model="site.spam_score"
                      type="number"
                      placeholder="Spam Score"
                      min="0"
                      max="100"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
            <b-button variant="outline-primary" size="sm" @click="addWebsite">
              + Add Website
            </b-button>
          </b-form-group>

          <!-- Niches -->
          <b-form-group label="Approved For Niche" label-for="niches" class="required-label">
            <validation-provider #default="{ errors }" name="Niches" vid="niches" rules="required">
              <v-select
                v-model="authorNiches"
                :options="nicheOptions"
                label="name"
                :reduce="(item) => item.id"
                multiple
                placeholder="Select niches"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group label="Notes" label-for="notes">
            <b-form-textarea
              id="notes"
              v-model="authorNotes"
              placeholder="Internal notes"
              rows="3"
            />
          </b-form-group>

          <template v-if="isSubmitLoading">
            <b-button class="float-right" variant="primary" disabled>
              <b-spinner small />
              Loading...
            </b-button>
          </template>
          <template v-else>
            <b-button
              type="submit"
              class="float-right"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
            >
              Submit
            </b-button>
          </template>
        </b-form>
      </validation-observer>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard,
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BButton,
  BForm,
  BModal,
  BSpinner,
  BRow,
  BCol,
  BFormTextarea,
  VBTooltip,
} from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, max } from "@validations";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { mapGetters } from "vuex";
import vSelect from "vue-select";
import {
  OUTREACH_AUTHOR_ACCESS,
  OUTREACH_AUTHOR_CREATE,
  OUTREACH_AUTHOR_EDIT,
  OUTREACH_AUTHOR_SHOW,
  OUTREACH_AUTHOR_DELETE,
  OUTREACH_AUTHOR_REVIEW,
} from "@/helpers/permissionsConstant";

export default {
  name: "OutreachAuthorView",
  components: {
    BForm,
    BButton,
    BCard,
    VueGoodTable,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    ValidationProvider,
    ValidationObserver,
    BModal,
    BSpinner,
    BRow,
    BCol,
    BFormTextarea,
    VBTooltip,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  data() {
    return {
      OUTREACH_AUTHOR_ACCESS,
      OUTREACH_AUTHOR_CREATE,
      OUTREACH_AUTHOR_EDIT,
      OUTREACH_AUTHOR_SHOW,
      OUTREACH_AUTHOR_DELETE,
      OUTREACH_AUTHOR_REVIEW,

      modelType: "",
      authorId: "",
      authorName: "",
      authorContact: "",
      authorWebsites: [{ url: "" }],
      authorNiches: [],
      authorNotes: "",

      nicheOptions: [],

      pageLength: 10,
      columns: [
        { label: "Name", field: "name", sortable: false },
        { label: "Status", field: "format_status", sortable: false },
        { label: "Contact", field: "format_contact", sortable: false },
        { label: "Total Payment", field: "total_payment", sortable: false },
        { label: "Posts (Used/Total)", field: "format_posts", sortable: false },
        { label: "Avg Per Post Cost", field: "per_post_cost", sortable: false },
        { label: "Action", field: "action", sortable: false },
      ],
      rows: [],
      searchTerm: "",
      delayTimer: null,
      isLoading: false,
      isSubmitLoading: false,
      totalRecords: 0,
      serverParams: {
        columnFilters: {},
        sort: [{ field: "id", type: "desc" }],
        page: 1,
        perPage: 10,
      },
    };
  },

  computed: {
    ...mapGetters({
      permissions: "userModule/getPermissions",
    }),
    statusVariant() {
      const statusColor = {
        pending: "light-primary",
        approved: "light-success",
        rejected: "light-danger",
      };
      return (status) => statusColor[status] || "light-secondary";
    },
  },

  async created() {
    this.loadItems();
    this.loadNiches();
  },

  methods: {
    addWebsite() {
      this.authorWebsites.push({ url: "", domain_authority: "", traffic: "", traffic_value: "", spam_score: "" });
    },
    removeWebsite(index) {
      this.authorWebsites.splice(index, 1);
    },

    async loadNiches() {
      try {
        const response = await this.$api.get("/api/outreach/approverfor/niche/all");
        this.nicheOptions = response.data.data || [];
      } catch (error) {
        // silent
      }
    },

    showCreateModal() {
      this.modelType = "createModel";
      this.$bvModal.show("modal-author-form");
    },

    hiddenModal() {
      this.$bvModal.hide("modal-author-form");
      this.resetModal();
    },

    resetModal() {
      this.modelType = "";
      this.authorId = "";
      this.authorName = "";
      this.authorContact = "";
      this.authorWebsites = [{ url: "", domain_authority: "", traffic: "", traffic_value: "", spam_score: "" }];
      this.authorNiches = [];
      this.authorNotes = "";
    },

    onEdit(value) {
      this.modelType = "editModel";
      this.authorId = value.id;
      this.authorName = value.name;
      this.authorContact = value.contact;
      this.authorNotes = value.notes;

      if (value.websites && value.websites.data) {
        this.authorWebsites = value.websites.data.map((w) => ({
          url: w.url,
          domain_authority: w.domain_authority || "",
          traffic: w.traffic || "",
          traffic_value: w.traffic_value || "",
          spam_score: w.spam_score || "",
        }));
      }
      if (value.niches && value.niches.data) {
        this.authorNiches = value.niches.data.map((n) => n.id);
      }

      this.$bvModal.show("modal-author-form");
    },

    viewDetail(value) {
      this.$router.push({ name: "outreach-author-detail", params: { id: value.id } });
    },
    onRowClick(params) {
      if (params?.column?.field === "action") return;
      this.viewDetail(params.row);
    },

    async onDelete(row) {
      this.$swal({
        title: "Warning!",
        text: "Are You Sure You Want To Delete " + row.name + "?",
        icon: "warning",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: "Ok",
        showLoaderOnConfirm: true,
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await this.$api.delete(`/api/outreach-authors/${row.id}`);
            this.loadItems();
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                icon: "BellIcon",
                variant: "success",
                text: "Author Successfully Deleted",
              },
            });
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Error",
                icon: "BellIcon",
                variant: "danger",
                text: error?.response?.data?.message,
              },
            });
          }
        }
      });
    },

    onSearch() {
      if (this.delayTimer) {
        clearTimeout(this.delayTimer);
        this.delayTimer = null;
      }
      this.delayTimer = setTimeout(() => {
        this.loadItems();
      }, 1000);
    },

    onStatusChange(row, status) {
      const title = status === "approved" ? "Approve" : "Reject";
      this.$bvModal.msgBoxConfirm(`Are you sure you want to ${title} this author?`, {
        title,
        size: "sm",
        okVariant: status === "approved" ? "success" : "danger",
        okTitle: title,
        cancelTitle: "Cancel",
        centered: true,
      }).then(async (confirmed) => {
        if (confirmed) {
          try {
            await this.$api.put(`/api/outreach-authors/${row.id}/status`, {
              status,
            });
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                icon: "BellIcon",
                variant: "success",
                text: `Author ${title}d Successfully`,
              },
            });
            this.loadItems();
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Error",
                icon: "BellIcon",
                variant: "danger",
                text: error?.response?.data?.message,
              },
            });
          }
        }
      });
    },

    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps);
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onColumnFilter(params) {
      this.updateParams(params);
      this.loadItems();
    },

    onPerPageChange(params) {
      this.updateParams({ perPage: params.currentPerPage });
      this.loadItems();
    },

    onSortChange(params) {
      this.updateParams({ sort: params });
      this.loadItems();
    },

    async loadItems() {
      try {
        this.isLoading = true;
        const response = await this.$api.get("/api/outreach-authors", {
          params: {
            show: this.serverParams.perPage,
            page: this.serverParams.page,
            sort: this.serverParams.sort,
            q: this.searchTerm,
          },
        });

        const data = response?.data?.data;
        const meta = response?.data?.meta;

        this.totalRecords = meta?.pagination?.total;
        this.rows = data;
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Warning",
            icon: "BellIcon",
            variant: "warning",
            text: error?.response?.data?.message,
          },
        });
      }
    },

    validationForm: async function () {
      this.$refs.authorValidation.validate().then(async (success) => {
        if (success) {
          try {
            this.$refs.authorValidation.reset();
            const payload = {
              name: this.authorName,
              contact: this.authorContact,
              websites: this.authorWebsites.filter((w) => w.url),
              niches: this.authorNiches,
              notes: this.authorNotes,
            };

            this.isSubmitLoading = true;

            if (this.modelType == "editModel") {
              await this.$api.put(`/api/outreach-authors/${this.authorId}`, payload);
            } else {
              await this.$api.post("/api/outreach-authors", payload);
            }

            this.isSubmitLoading = false;
            this.hiddenModal();
            this.loadItems();

            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                icon: "BellIcon",
                variant: "success",
                text: this.modelType == "editModel" ? "Author Updated" : "Author Created",
              },
            });
          } catch (error) {
            this.isSubmitLoading = false;
            if (error?.response?.data?.message) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Error",
                  icon: "BellIcon",
                  variant: "danger",
                  text: error?.response?.data?.message,
                },
              });
            }
            if (error?.response?.data?.errors) {
              this.$refs.authorValidation.setErrors(error?.response?.data?.errors);
            }
          }
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";

.required-label label::after {
  content: " *";
  color: red;
}

.clickable-rows tbody tr {
  cursor: pointer;
}
</style>
